import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { api } from "@/lib/api";
import type { InstanceOutreachUsage } from "@/types/api";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

function minutes(
  t: TFunction<"instances">,
  durationTotalSeconds: number
): string {
  return t("voiceUsage.minutesValue", {
    value: Math.round(durationTotalSeconds / 60),
  });
}

function usd(costTotal: number): string {
  return `$${costTotal.toFixed(2)}`;
}

function statusVariant(
  status: string
): "success" | "destructive" | "secondary" {
  if (status === "running") return "success";
  if (status === "failed") return "destructive";
  return "secondary";
}

/**
 * "Voice usage" card on the instance page: per-campaign minutes + provider
 * cost, live-proxied from the instance's own DB. Operator-only — the tenant UI
 * shows minutes but never cost.
 */
export function InstanceVoiceUsageSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const { data, isLoading } = useQuery({
    queryKey: ["instance", name, "outreach-usage"],
    queryFn: () =>
      api.get<InstanceOutreachUsage>(
        `/api/admin/instances/${name}/outreach-usage`
      ),
  });

  const campaigns = data?.campaigns ?? [];
  const totals = data?.totals ?? null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("voiceUsage.title")}</CardTitle>
        <CardDescription>
          {t("voiceUsage.description")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : data?.unreachable ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t("voiceUsage.unreachable")}
          </p>
        ) : campaigns.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            {t("voiceUsage.empty")}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("voiceUsage.columns.campaign")}</TableHead>
                <TableHead>{t("voiceUsage.columns.status")}</TableHead>
                <TableHead className="text-right">{t("voiceUsage.columns.attempted")}</TableHead>
                <TableHead className="text-right">{t("voiceUsage.columns.connected")}</TableHead>
                <TableHead className="text-right">{t("voiceUsage.columns.minutes")}</TableHead>
                <TableHead className="text-right">{t("voiceUsage.columns.cost")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.map((c) => (
                <TableRow key={c.id}>
                  <TableCell className="max-w-[16rem] truncate font-medium">
                    {c.name}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(c.status)}>{c.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.attempted}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {c.connected}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {minutes(t, c.durationTotalSeconds)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {usd(c.costTotal)}
                  </TableCell>
                </TableRow>
              ))}
              {totals ? (
                <TableRow className="font-semibold">
                  <TableCell>{t("voiceUsage.total")}</TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                  <TableCell className="text-right tabular-nums">
                    {minutes(t, totals.durationTotalSeconds)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {usd(totals.costTotal)}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
